import { CihSectionHeader } from "~/components/cih/section-header";
import { MarkdownContent } from "~/components/markdown-content";

type Challenge = {
  slug: string;
  title: string;
  description?: string | null;
  typeLabel?: string;
};

export type HelpsWithProps = {
  firstName: string;
  /**
   * Profile-level challenges content. Both fields are optional. The section is
   * profile-driven (not offer-driven) so it's available to every coach, not
   * just those with Feature.OfferPage. The offer detail page renders its own
   * struggles fields independently.
   */
  challengesHeadline: string | null;
  challengesDescription: string | null;
  /**
   * CIH challenge tiles — secondary tile grid below the prose. Used as a
   * standalone fallback when no prose is set; otherwise a supplementary grid
   * for SEO and internal linking.
   */
  cihChallenges: Challenge[];
  sectionNumber: number;
};

export function CoachHelpsWithSection({
  firstName,
  challengesHeadline,
  challengesDescription,
  cihChallenges,
  sectionNumber,
}: HelpsWithProps) {
  const intro = challengesDescription?.trim() || null;
  const tiles = cihChallenges.slice(0, 3);
  const hasTiles = tiles.length > 0;

  if (!intro && !hasTiles) return null;

  const headline = intro
    ? challengesHeadline?.trim() || "What I help clients work through"
    : `What ${firstName} helps with`;

  return (
    <section id="helps-with" className="border-t border-gray-200 bg-purple-50">
      <CihSectionHeader
        label={`${String(sectionNumber).padStart(2, "0")} · Challenges`}
        title={headline}
      />
      <div className="mx-auto max-w-7xl px-4 pb-14 md:pb-20 xl:px-0">
        {intro && (
          <div className="max-w-prose text-gray-700">
            <MarkdownContent ugc content={intro} />
          </div>
        )}
        {hasTiles && (
          <div className={`grid gap-6 md:grid-cols-3 ${intro ? "mt-10" : ""}`}>
            {tiles.map((c) => (
              <a
                key={c.slug}
                href={`/coaching-for/${c.slug}`}
                className="rounded-xl border border-gray-200 bg-white p-5 transition-colors hover:border-gray-300"
              >
                {c.typeLabel && (
                  <div className="text-[10px] font-medium tracking-[0.16em] text-purple-700 uppercase">
                    {c.typeLabel}
                  </div>
                )}
                <h3 className="mt-2 font-heading text-xl font-medium tracking-tight text-gray-900">
                  {c.title}
                </h3>
                {c.description && (
                  <p className="mt-2 line-clamp-2 text-sm text-gray-600">
                    {c.description}
                  </p>
                )}
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
